import { FileText, FileUp, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { DeleteConfirmDialog } from "./DeleteConfirmDialog";
import { KnowledgeFiltersDrawer } from "./KnowledgeFiltersDrawer";
import { SyncStatusBadge } from "./SyncStatusBadge";
import {
  useDeleteDocument,
  useKnowledgeDocuments,
  useKnowledgeFilterCounts,
  useSyncKnowledge,
} from "../api/useKnowledgeApi";
import { useFileDrop } from "../hooks/useFileDrop";
import type { KnowledgeDocumentSummary } from "../types";

import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { RelativeTime } from "@/components/ui/v2/relative-time";
import { DataTable } from "@/components/ui/v2/table/data-table";
import {
  TableActiveFilters,
  type ActiveFilter,
} from "@/components/ui/v2/table/table-active-filters";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";
import { useAuth } from "@/lib/hooks/useAuth";
import { usePageSearchParams } from "@/lib/hooks/usePageSearchParams";
import { usePageSizeSearchParams } from "@/lib/hooks/usePageSizeSearchParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";
import type { TableColumn } from "@/types/table/tableProps";

type KnowledgeBaseManagerProps = {
  integrationName: string;
};

export function KnowledgeBaseManager({
  integrationName,
}: KnowledgeBaseManagerProps) {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const navigate = useNavigate();

  const { page, setPage } = usePageSearchParams();
  const { pageSize, setPageSize } = usePageSizeSearchParams();
  const { searchValue, debouncedSearchValue, setSearchValue } =
    useSearchSearchParams();
  const { orderBy, descending } = useSortSearchParams();
  const handleSort = useTableSortHandler<KnowledgeDocumentSummary>();

  const [syncStatusFilter, setSyncStatusFilter] = useState<string[]>([]);
  const [chunkTypeFilter, setChunkTypeFilter] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data, isLoading, isFetching } = useKnowledgeDocuments({
    page,
    pageSize,
    search: debouncedSearchValue || undefined,
    syncStatus: syncStatusFilter.length === 1 ? syncStatusFilter[0] : undefined,
    chunkType: chunkTypeFilter.length === 1 ? chunkTypeFilter[0] : undefined,
    orderBy: orderBy ?? undefined,
    descending: descending ?? undefined,
  });

  const syncMutation = useSyncKnowledge(tenantId);
  const deleteMutation = useDeleteDocument();
  const { data: filterCounts } = useKnowledgeFilterCounts();

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const handleCreate = useCallback(() => {
    navigate(`/integrations/${integrationName}/chat/new`);
  }, [navigate, integrationName]);

  const handleRowClick = useCallback(
    (row: KnowledgeDocumentSummary) => {
      navigate(
        `/integrations/${integrationName}/chat/${encodeURIComponent(row.fileName)}`,
      );
    },
    [navigate, integrationName],
  );

  const handleFileDrop = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const content = reader.result as string;
        const params = new URLSearchParams({
          fileName: file.name,
          title: file.name.replace(/\.md$/, "").replace(/[-_]/g, " "),
          content,
        });
        navigate(`/integrations/${integrationName}/chat/new?${params}`);
      };
      reader.readAsText(file);
    },
    [navigate, integrationName],
  );

  const { isDragging, dropProps } = useFileDrop({ onFileDrop: handleFileDrop });

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget, {
      onSuccess: () => {
        syncMutation.mutate(undefined, {
          onSettled: () => setDeleteTarget(null),
        });
      },
    });
  }, [deleteTarget, deleteMutation, syncMutation]);

  const columns = useMemo<TableColumn<KnowledgeDocumentSummary>[]>(
    () => [
      {
        accessor: "title",
        header: t("captain.knowledge.titleLabel"),
        sortable: true,
        Cell: (row: KnowledgeDocumentSummary) => (
          <div
            className="max-w-[500px] truncate font-medium"
            title={row.title || row.fileName}
          >
            {row.title || row.fileName}
          </div>
        ),
      },
      {
        accessor: "chunkType",
        header: t("captain.knowledge.typeLabel"),
        sortable: true,
        Cell: (row: KnowledgeDocumentSummary) =>
          row.chunkType ? (
            <Badge variant="outline">{row.chunkType}</Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessor: "syncStatus",
        header: t("captain.knowledge.syncStatus"),
        sortable: true,
        Cell: (row: KnowledgeDocumentSummary) => (
          <SyncStatusBadge status={row.syncStatus} />
        ),
      },
      {
        accessor: "lastModified",
        header: t("captain.knowledge.lastModified"),
        sortable: true,
        Cell: (row: KnowledgeDocumentSummary) => (
          <RelativeTime date={row.lastModified} />
        ),
      },
      {
        accessor: "fileName",
        header: "",
        width: 80,
        Cell: (row: KnowledgeDocumentSummary) => (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(row.fileName);
              }}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ),
      },
    ],
    [t],
  );

  const documents = data?.documents ?? [];
  const totalRecords = data?.total ?? 0;

  const SYNC_LABELS: Record<string, string> = {
    synced: "Synced",
    pending: "Pending",
    new: "New",
  };
  const TYPE_LABELS: Record<string, string> = {
    "domain-knowledge": "Domain Knowledge",
    "q-and-a": "Q&A",
    example: "Example",
    procedure: "Procedure",
  };

  const activeFilters: ActiveFilter[] = [
    ...syncStatusFilter.map((v) => ({
      key: "syncStatus",
      label: t("captain.knowledge.syncStatus"),
      value: v,
      valueLabel: SYNC_LABELS[v] ?? v,
    })),
    ...chunkTypeFilter.map((v) => ({
      key: "chunkType",
      label: t("captain.knowledge.typeLabel"),
      value: v,
      valueLabel: TYPE_LABELS[v] ?? v,
    })),
  ];

  return (
    <div className="space-y-4" {...dropProps}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">
            {t("captain.knowledge.title")}
          </h2>
          {data && (
            <Badge variant="neutral" size="medium">
              {data.total}{" "}
              {data.total === 1
                ? t("common.document", { defaultValue: "document" })
                : t("common.documents", { defaultValue: "documents" })}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            {t("captain.knowledge.newDocumentButton")}
          </Button>
          <Button
            size="sm"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${syncMutation.isPending ? "animate-spin" : ""}`}
            />
            {syncMutation.isPending
              ? t("captain.knowledge.syncing")
              : t("captain.knowledge.syncToCaption")}
          </Button>
        </div>
      </div>

      <TableToolbar
        onSearchChange={setSearchValue}
        searchValue={searchValue}
        searchPlaceholder={t("captain.knowledge.searchPlaceholder")}
        onFiltersClick={() => setFiltersOpen(true)}
      />

      {activeFilters.length > 0 && (
        <TableActiveFilters
          filters={activeFilters}
          onRemoveFilter={(filter) => {
            if (filter.key === "syncStatus") {
              setSyncStatusFilter((prev) =>
                prev.filter((v) => v !== filter.value),
              );
            } else if (filter.key === "chunkType") {
              setChunkTypeFilter((prev) =>
                prev.filter((v) => v !== filter.value),
              );
            }
            setPage(1);
          }}
          onClearFilters={() => {
            setSyncStatusFilter([]);
            setChunkTypeFilter([]);
            setPage(1);
          }}
        />
      )}

      {isDragging && (
        <div className="flex items-center justify-center rounded-lg border-2 border-dashed border-primary/50 bg-primary/5 p-12">
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <FileUp className="h-8 w-8" />
            <p className="text-sm">{t("captain.knowledge.dropHere")}</p>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={documents}
        isLoading={isLoading || isFetching}
        onRowClick={handleRowClick}
        orderBy={orderBy ?? undefined}
        descending={descending ?? undefined}
        onSort={handleSort}
        page={page}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        getRowId={(row) => row.fileName}
        emptyState={{
          icon: FileText,
          title: t("captain.knowledge.noDocuments"),
          description: t("captain.knowledge.noDocumentsDescription"),
          action: {
            label: t("captain.knowledge.newDocumentButton"),
            onClick: handleCreate,
            icon: <Plus className="h-4 w-4" />,
          },
        }}
      />

      {deleteTarget && (
        <DeleteConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(open) =>
            !open &&
            !deleteMutation.isPending &&
            !syncMutation.isPending &&
            setDeleteTarget(null)
          }
          fileName={deleteTarget}
          onConfirm={handleDelete}
          isLoading={deleteMutation.isPending || syncMutation.isPending}
        />
      )}

      <KnowledgeFiltersDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filters={{ syncStatus: syncStatusFilter, chunkType: chunkTypeFilter }}
        onApply={(f) => {
          setSyncStatusFilter(f.syncStatus);
          setChunkTypeFilter(f.chunkType);
          setPage(1);
        }}
        searchValue={debouncedSearchValue}
        counts={filterCounts}
      />
    </div>
  );
}
